import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router-dom";

import PageBanner from "../Components/PageBanner";
import { Range } from "react-range";
import PropertyListCard from "../Components/PropertyListCard";
import { getPropertyListData } from "../Constants/Data";

export default function Properties() {
  const location = useLocation();
  const navigate = useNavigate();

  const [propertyList, setPropertyList] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [perPage, setPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [priceRange, setPriceRange] = useState([0, 1000000000]);

  // Filtration
  const [sortOption, setSortOption] = useState("default");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedPurpose, setSelectedPurpose] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPropertyType, setSelectedPropertyType] = useState("");
  const [selectedBedrooms, setSelectedBedrooms] = useState("Choose Your Choice");
  const [selectedBathrooms, setSelectedBathrooms] = useState("Choose Your Choice");
  const [selectedState, setSelectedState] = useState("");
  const [selectedSuburb, setSelectedSuburb] = useState("");

  // Helper function to parse price guide - handles $ signs, commas, ranges, decimals
  const parsePriceGuide = (priceguide) => {
    if (!priceguide) return null;
    
    let priceStr = priceguide.toString().trim();
    
    // Remove dollar sign and any other currency symbols
    priceStr = priceStr.replace(/[$£€¥]/g, '');
    
    // Handle range like "725,000 - 740,000" or "$950,000 - $1,000,000"
    if (priceStr.includes('-')) {
      const match = priceStr.match(/([\d,]+(?:\.\d+)?)\s*-\s*([\d,]+(?:\.\d+)?)/);
      if (match) {
        // Return the minimum price for filtering
        const minPrice = parseFloat(match[1].replace(/,/g, ''));
        return isNaN(minPrice) ? null : minPrice;
      }
    }
    
    // Handle single price like "$950,000.00" or "500,000"
    // Remove commas and convert to number
    const cleanPrice = priceStr.replace(/,/g, '');
    const price = parseFloat(cleanPrice);
    
    return isNaN(price) ? null : price;
  };

  // Function to parse URL parameters
  const getQueryParams = () => {
    const params = new URLSearchParams(location.search);
    const stateFromUrl = params.get("state");

    return {
      state: stateFromUrl ? decodeURIComponent(stateFromUrl) : "",
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const data = await getPropertyListData();
      setPropertyList(data);
      setFilteredProperties(data);
      setLoading(false);
    };

    fetchData();
  }, []);

  // Initialize filters from URL when component mounts or URL changes
  useEffect(() => {
    const { state } = getQueryParams();
    if (state) {
      setSelectedState(state);
    } else {
      setSelectedState("");
    }
  }, [location.search]);

  // Single useEffect to handle ALL filtering
  useEffect(() => {
    if (!propertyList.length) return;

    let filteredData = [...propertyList];

    // 1. Apply Status filter
    if (selectedStatus) {
      filteredData = filteredData.filter(
        (property) => property.pstatus === selectedStatus
      );
    }

    // 2. Reset purpose if status is not "Available"
    if (selectedStatus !== "Available" && selectedPurpose !== "") {
      setSelectedPurpose("");
    }

    // 3. Apply Purpose filter only if status is "Available"
    if (selectedStatus === "Available" && selectedPurpose) {
      filteredData = filteredData.filter(
        (property) => property.preason === selectedPurpose
      );
    }

    // 4. Apply Search filter
    if (searchQuery) {
      const lowerSearchQuery = searchQuery.toLowerCase();
      filteredData = filteredData.filter(
        (property) =>
          property.address?.state?.toLowerCase().includes(lowerSearchQuery) ||
          property.address?.suburb?.toLowerCase().includes(lowerSearchQuery) ||
          property.ptitle?.toLowerCase().includes(lowerSearchQuery)
      );
    }

    // 5. Apply Property Type filter
    if (selectedPropertyType) {
      const normalizedSelectedType = selectedPropertyType.trim().toLowerCase();
      filteredData = filteredData.filter(
        (property) => {
          const propertyType = property.ptype?.trim().toLowerCase();
          return propertyType === normalizedSelectedType;
        }
      );
    }

    // 6. Apply Bedrooms filter
    if (selectedBedrooms !== "Choose Your Choice") {
      if (selectedBedrooms === "Studio") {
        filteredData = filteredData.filter((property) => property.beds === 0);
      } else {
        const minBeds = Number(selectedBedrooms.replace("+", ""));
        filteredData = filteredData.filter(
          (property) => property.beds >= minBeds
        );
      }
    }

    // 7. Apply Bathrooms filter
    if (selectedBathrooms !== "Choose Your Choice") {
      const minBaths = Number(selectedBathrooms.replace("+", ""));
      filteredData = filteredData.filter(
        (property) => property.baths >= minBaths
      );
    }

    // 8. Apply State filter
    if (selectedState) {
      filteredData = filteredData.filter(
        (property) => property.address?.state === selectedState
      );
    }

    // 9. Apply Suburb filter
    if (selectedSuburb) {
      filteredData = filteredData.filter(
        (property) => property.address?.suburb === selectedSuburb
      );
    }

    // 10. Apply Price Range filter - Uses updated parsePriceGuide
    filteredData = filteredData.filter((property) => {
      const propertyPrice = parsePriceGuide(property.priceguide);
      if (propertyPrice === null || isNaN(propertyPrice)) return false;
      return propertyPrice >= priceRange[0] && propertyPrice <= priceRange[1];
    });

    // 11. Apply Sorting - Uses updated parsePriceGuide
    if (sortOption !== "default") {
      filteredData.sort((a, b) => {
        const priceA = parsePriceGuide(a.priceguide);
        const priceB = parsePriceGuide(b.priceguide);

        if (priceA === null && priceB === null) return 0;
        if (priceA === null) return 1;
        if (priceB === null) return -1;

        return sortOption === "price-low" ? priceA - priceB : priceB - priceA;
      });
    }

    setFilteredProperties(filteredData);

    // Keep current page valid after filtering
    const updatedTotalPages = Math.ceil(filteredData.length / perPage) || 1;
    if (currentPage > updatedTotalPages) {
      setCurrentPage(updatedTotalPages);
    } else if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [
    propertyList,
    selectedStatus,
    selectedPurpose,
    searchQuery,
    selectedPropertyType,
    selectedBedrooms,
    selectedBathrooms,
    selectedState,
    selectedSuburb,
    priceRange,
    sortOption,
    perPage,
    currentPage,
  ]);

  // Helper function to update URL parameters
  const updateUrlParams = (params) => {
    const urlParams = new URLSearchParams(location.search);

    Object.keys(params).forEach((key) => {
      if (params[key]) {
        urlParams.set(key, encodeURIComponent(params[key]));
      } else {
        urlParams.delete(key);
      }
    });

    navigate(`?${urlParams.toString()}`, { replace: true });
  };

  // Calculate unique values for dropdowns
  const uniquePropertyTypes = useMemo(() => {
    return propertyList.reduce((acc, property) => {
      const normalizedType = property.ptype?.trim();
      if (!normalizedType) return acc;
      
      const existingType = acc.find(
        (item) => item.label.toLowerCase() === normalizedType.toLowerCase()
      );
      
      if (existingType) {
        existingType.count += 1;
      } else {
        acc.push({ label: normalizedType, count: 1 });
      }
      return acc;
    }, []);
  }, [propertyList]);

  const uniqueStates = useMemo(() => {
    return [...new Set(propertyList.map((property) => property.address?.state))].filter(Boolean);
  }, [propertyList]);

  const uniqueSuburbs = useMemo(() => {
    return [...new Set(propertyList.map((property) => property.address?.suburb))].filter(Boolean);
  }, [propertyList]);

  const uniqueStatuses = useMemo(() => {
    return [...new Set(propertyList.map((property) => property.pstatus))].filter(Boolean);
  }, [propertyList]);

  const uniquePurpose = useMemo(() => {
    return [...new Set(propertyList.map((property) => property.preason))].filter(Boolean);
  }, [propertyList]);

  // Reset functions
  const resetFilters = () => {
    setSelectedPropertyType("");
    setSelectedBedrooms("Choose Your Choice");
    setSelectedBathrooms("Choose Your Choice");
    setSelectedState("");
    setSelectedSuburb("");
    setPriceRange([0, 1000000000]);
    setSelectedStatus("");
    setSelectedPurpose("");
    setSearchQuery("");
    setSortOption("default");
    setPerPage(5);
    setCurrentPage(1);

    // Clear all URL parameters
    navigate("/properties", { replace: true });
  };

  const resetSortingAndFilters = () => {
    setSortOption("default");
    setPerPage(5);
    setCurrentPage(1);
    setSelectedStatus("");
    setSelectedPurpose("");
    setSearchQuery("");
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredProperties.length / perPage);

  const paginatedProperties = filteredProperties.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  // Handle page change
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Improved sliding pagination
  const getVisiblePages = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let startPage = currentPage;
    let endPage = currentPage + 2;

    if (endPage > totalPages - 2) {
      endPage = totalPages - 2;
      startPage = endPage - 2;
    }

    if (startPage < 1) {
      startPage = 1;
      endPage = 3;
    }

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      if (i > 0 && i <= totalPages) {
        pages.push(i);
      }
    }

    return pages;
  };

  const visiblePages = getVisiblePages();

  // Handler for state filter change
  const handleStateChange = (stateValue) => {
    setSelectedState(stateValue);

    if (stateValue) {
      updateUrlParams({ state: stateValue });
    } else {
      updateUrlParams({ state: "" });
    }

    setCurrentPage(1);
  };

  const renderDesktopAdvancedFilters = () => (
    <>
      <h5 className="brand fw-bold">Advance Information</h5>
      <p className="small">
        About {propertyList.length.toLocaleString()} results
      </p>
      <p className="small text-primary">
        Showing {filteredProperties.length} filtered results
      </p>

      <div className="mt-3 bg-light p-3 border">
        {/* Property Type */}
        <div className="mb-4">
          <h6 className="fw-bold">Property Type</h6>
          {uniquePropertyTypes.map((item, index) => (
            <div key={index} className="d-flex justify-content-between">
              <div>
                <input
                  type="radio"
                  name="propertyTypeFilterDesktop"
                  id={`desktop-property-${index}`}
                  className="me-2"
                  value={item.label}
                  checked={selectedPropertyType === item.label}
                  onChange={(e) => {
                    setSelectedPropertyType(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <label htmlFor={`desktop-property-${index}`}>
                  {item.label} ({item.count})
                </label>
              </div>
            </div>
          ))}
        </div>

        {/* Bed/Bath Filter */}
        <div className="mb-4">
          <h6 className="fw-bold">Bed</h6>
          <select
            className="form-select mb-2 rounded-0"
            value={selectedBedrooms}
            onChange={(e) => {
              setSelectedBedrooms(e.target.value);
              setCurrentPage(1);
            }}
          >
            {[
              "Choose Your Choice",
              "Studio",
              "1+",
              "2+",
              "3+",
              "4+",
              "5+",
              "6+",
            ].map((range, index) => (
              <option key={index} value={range}>
                {range}
              </option>
            ))}
          </select>

          <h6 className="fw-bold">Bath</h6>
          <select
            className="form-select rounded-0"
            value={selectedBathrooms}
            onChange={(e) => {
              setSelectedBathrooms(e.target.value);
              setCurrentPage(1);
            }}
          >
            {[
              "Choose Your Choice",
              "1+",
              "2+",
              "3+",
              "4+",
              "5+",
              "6+",
            ].map((range, index) => (
              <option key={index} value={range}>
                {range}
              </option>
            ))}
          </select>
        </div>

        {/* State Filter */}
        <div className="mb-4">
          <h6 className="fw-bold">State</h6>
          <div className="d-flex justify-content-between">
            <div>
              <input
                type="radio"
                name="stateFilterDesktop"
                id="desktop-state-all"
                className="me-2"
                value=""
                checked={selectedState === ""}
                onChange={() => handleStateChange("")}
              />
              <label htmlFor="desktop-state-all">All States</label>
            </div>
          </div>

          {uniqueStates.map((state, index) => (
            <div key={index} className="d-flex justify-content-between">
              <div>
                <input
                  type="radio"
                  name="stateFilterDesktop"
                  id={`desktop-state-${index}`}
                  className="me-2"
                  value={state}
                  checked={selectedState === state}
                  onChange={(e) => handleStateChange(e.target.value)}
                />
                <label htmlFor={`desktop-state-${index}`}>{state}</label>
              </div>
            </div>
          ))}
        </div>

        {/* Suburb Filter */}
        <div className="mb-4">
          <h6 className="fw-bold">Suburb</h6>
          <select
            className="form-select w-100 rounded-0"
            value={selectedSuburb}
            onChange={(e) => {
              setSelectedSuburb(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Suburbs</option>
            {uniqueSuburbs.map((suburb, index) => (
              <option key={index} value={suburb}>
                {suburb}
              </option>
            ))}
          </select>
        </div>

        {/* Price Range Slider */}
        <div className="mb-4">
          <h6 className="fw-bold">Filter By Price</h6>
          <p className="small pb-3">
            Your range:{" "}
            <strong>
              ${priceRange[0].toLocaleString()} - $
              {priceRange[1].toLocaleString()}
            </strong>
          </p>

          <Range
            step={100000}
            min={0}
            max={1000000000}
            values={priceRange}
            onChange={(values) => {
              setPriceRange(values);
              setCurrentPage(1);
            }}
            renderTrack={({ props, children }) => {
              const { key, ...restProps } = props;
              return (
                <div
                  {...restProps}
                  key={key}
                  style={{
                    height: "2px",
                    width: "100%",
                    background: "rgba(0,0,0,0.3)",
                    position: "relative",
                    marginTop: "10px",
                  }}
                >
                  {children}
                </div>
              );
            }}
            renderThumb={({ props }) => {
              const { key, ...restProps } = props;
              return (
                <div
                  {...restProps}
                  key={key}
                  style={{
                    height: "14px",
                    width: "14px",
                    backgroundColor: "#00b0f7",
                    borderRadius: "50%",
                    marginTop: "-14px",
                    cursor: "pointer",
                  }}
                />
              );
            }}
          />
        </div>

        <button
          className="btn btn-outline-primary rounded-0 w-100 mt-3"
          onClick={resetFilters}
        >
          Reset All Filters
        </button>
      </div>
    </>
  );

  const renderMobileTabletAdvancedFilters = () => (
    <>
      <h5 className="brand fw-bold">Advance Information</h5>
      <p className="small">
        About {propertyList.length.toLocaleString()} results
      </p>
      <p className="small text-primary">
        Showing {filteredProperties.length} filtered results
      </p>

      <div className="mt-3 bg-light p-3 border">
        <div className="row g-4">
          {/* Row 1 */}
          <div className="col-12 col-md-6">
            <h6 className="fw-bold">Property Type</h6>
            {uniquePropertyTypes.map((item, index) => (
              <div key={index} className="d-flex justify-content-between">
                <div>
                  <input
                    type="radio"
                    name="propertyTypeFilterMobile"
                    id={`mobile-property-${index}`}
                    className="me-2"
                    value={item.label}
                    checked={selectedPropertyType === item.label}
                    onChange={(e) => {
                      setSelectedPropertyType(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                  <label htmlFor={`mobile-property-${index}`}>
                    {item.label} ({item.count})
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className="col-12 col-md-6">
            <h6 className="fw-bold">Bed</h6>
            <select
              className="form-select mb-3 rounded-0"
              value={selectedBedrooms}
              onChange={(e) => {
                setSelectedBedrooms(e.target.value);
                setCurrentPage(1);
              }}
            >
              {[
                "Choose Your Choice",
                "Studio",
                "1+",
                "2+",
                "3+",
                "4+",
                "5+",
                "6+",
              ].map((range, index) => (
                <option key={index} value={range}>
                  {range}
                </option>
              ))}
            </select>

            <h6 className="fw-bold">Bath</h6>
            <select
              className="form-select rounded-0"
              value={selectedBathrooms}
              onChange={(e) => {
                setSelectedBathrooms(e.target.value);
                setCurrentPage(1);
              }}
            >
              {[
                "Choose Your Choice",
                "1+",
                "2+",
                "3+",
                "4+",
                "5+",
                "6+",
              ].map((range, index) => (
                <option key={index} value={range}>
                  {range}
                </option>
              ))}
            </select>
          </div>

          {/* Row 2 */}
          <div className="col-12 col-md-6">
            <h6 className="fw-bold">State</h6>
            <div className="d-flex justify-content-between">
              <div>
                <input
                  type="radio"
                  name="stateFilterMobile"
                  id="mobile-state-all"
                  className="me-2"
                  value=""
                  checked={selectedState === ""}
                  onChange={() => handleStateChange("")}
                />
                <label htmlFor="mobile-state-all">All States</label>
              </div>
            </div>

            {uniqueStates.map((state, index) => (
              <div key={index} className="d-flex justify-content-between">
                <div>
                  <input
                    type="radio"
                    name="stateFilterMobile"
                    id={`mobile-state-${index}`}
                    className="me-2"
                    value={state}
                    checked={selectedState === state}
                    onChange={(e) => handleStateChange(e.target.value)}
                  />
                  <label htmlFor={`mobile-state-${index}`}>{state}</label>
                </div>
              </div>
            ))}
          </div>

          <div className="col-12 col-md-6">
            <h6 className="fw-bold">Suburb</h6>
            <select
              className="form-select w-100 rounded-0 mb-4"
              value={selectedSuburb}
              onChange={(e) => {
                setSelectedSuburb(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Suburbs</option>
              {uniqueSuburbs.map((suburb, index) => (
                <option key={index} value={suburb}>
                  {suburb}
                </option>
              ))}
            </select>

            <h6 className="fw-bold">Filter By Price</h6>
            <p className="small pb-3 mb-0">
              Your range:{" "}
              <strong>
                ${priceRange[0].toLocaleString()} - $
                {priceRange[1].toLocaleString()}
              </strong>
            </p>

            <Range
              step={100000}
              min={0}
              max={1000000000}
              values={priceRange}
              onChange={(values) => {
                setPriceRange(values);
                setCurrentPage(1);
              }}
              renderTrack={({ props, children }) => {
                const { key, ...restProps } = props;
                return (
                  <div
                    {...restProps}
                    key={key}
                    style={{
                      height: "2px",
                      width: "100%",
                      background: "rgba(0,0,0,0.3)",
                      position: "relative",
                      marginTop: "10px",
                    }}
                  >
                    {children}
                  </div>
                );
              }}
              renderThumb={({ props }) => {
                const { key, ...restProps } = props;
                return (
                  <div
                    {...restProps}
                    key={key}
                    style={{
                      height: "14px",
                      width: "14px",
                      backgroundColor: "#00b0f7",
                      borderRadius: "50%",
                      marginTop: "-14px",
                      cursor: "pointer",
                    }}
                  />
                );
              }}
            />
          </div>

          {/* Row 3 */}
          <div className="col-12">
            <button
              className="btn btn-outline-primary rounded-0 w-100"
              onClick={resetFilters}
            >
              Reset All Filters
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Helmet>
        <title>Properties | Urban View Real Estate PVT LTD</title>
        <meta
          name="description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta
          name="keywords"
          content="Affordable Homes for Sale in South Australia, Affordable Homes for Sale in Melbourne, Affordable Homes for Sale in Queensland, Development Sites for Sale in Sydney, Cheap Rent a Home in Penrith, House Buying in Western Australia, Cheap Duplex Home in Tamworth, Affordable duplex Home in Armidale, Houses for Rental in St Marys, Investement Property in Queensland, Best Investment Property Sale in Brisbane, Best Investment Property Sale in Rouse Hill, Box Hill, Marsden Park, Vineyard, Investment Property Buy in Austral, Investment Property Buy in Tallawong, Investment Property Buy in Oran Park, Investment Property Buy in Leppington"
        />
        <meta name="author" content="Urban View Real Estate PVT LTD" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://urbanviewre.com/properties" />

        <meta property="og:title" content="Urban View Real Estate PVT LTD" />
        <meta
          property="og:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta property="og:site_name" content="Urban View Real Estate" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urbanviewre.com/properties" />
        <meta
          property="og:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Urban View Real Estate PVT LTD" />
        <meta
          name="twitter:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta
          name="twitter:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />
        <meta name="twitter:site" content="@urbanviewre" />
        <meta name="twitter:creator" content="@urbanviewre" />

        <meta
          property="fb:admins"
          content="https://www.facebook.com/profile.php?id=61559035348971"
        />

        <meta name="instagram:title" content="Urban View Real Estate PVT LTD" />
        <meta
          name="instagram:description"
          content="Find affordable homes for sale & rent across Australia with Urban View Real Estate. Explore investment properties in Brisbane, Sydney suburbs, Queensland & Melbourne."
        />
        <meta name="instagram:site" content="@urbanviewrealestate" />
      </Helmet>

      <PageBanner
        title="Properties"
        breadcrumbs={[
          { label: "Home", href: "/", icon: "bi-house-door-fill" },
          { label: "Properties", active: true },
        ]}
      />

      <section className="py-5 mb-5">
        <div className="container">
          <div className="row">
            {/* Left/Main Content */}
            <div className="col-12 col-lg-9">
              <div className="row g-2 py-3">
                <div className="col-auto d-none d-lg-block">
                  <i className="bi bi-list-task fs-4 text-primary cursor-pointer ms-2"></i>
                </div>

                {/* Sorting Dropdown */}
                <div className="col-12 col-md-6 col-lg-auto">
                  <select
                    className="form-select w-100 rounded-0"
                    value={sortOption}
                    onChange={(e) => {
                      setSortOption(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="default">Default Sorting</option>
                    <option value="price-low">Price: Low to High</option>
                    <option value="price-high">Price: High to Low</option>
                  </select>
                </div>

                {/* Per Page Dropdown */}
                <div className="col-12 col-md-6 col-lg-auto">
                  <select
                    className="form-select w-100 rounded-0"
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="5">Per Page: 5</option>
                    <option value="12">Per Page: 12</option>
                    <option value="24">Per Page: 24</option>
                    <option value="48">Per Page: 48</option>
                  </select>
                </div>

                {/* Status Dropdown */}
                <div className="col-12 col-md-6 col-lg-auto">
                  <select
                    className="form-select w-100 rounded-0"
                    value={selectedStatus}
                    onChange={(e) => {
                      setSelectedStatus(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">Select Status</option>
                    {uniqueStatuses.map((status, index) => (
                      <option key={index} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reason Dropdown */}
                {selectedStatus === "Available" && (
                  <div className="col-12 col-md-6 col-lg-auto">
                    <select
                      className="form-select w-100 rounded-0"
                      value={selectedPurpose}
                      onChange={(e) => {
                        setSelectedPurpose(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Property Purpose</option>
                      {uniquePurpose.map((purpose, index) => (
                        <option key={index} value={purpose}>
                          {purpose}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="col-12 col-md-6 col-lg-auto">
                  <button
                    className="btn btn-primary rounded-0 w-100"
                    onClick={resetSortingAndFilters}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Reset
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <div className="input-group mb-4">
                <input
                  type="text"
                  className="form-control rounded-0 bg-light"
                  placeholder="Search by State or Title..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <button
                  className="btn btn-primary rounded-0"
                  onClick={() => {
                    setSearchQuery("");
                    setCurrentPage(1);
                  }}
                >
                  <i className="bi bi-search"></i>
                </button>
              </div>

              {/* Tablet/Mobile Advanced Filter */}
              <div className="d-block d-lg-none mb-4">
                {renderMobileTabletAdvancedFilters()}
              </div>

              {/* Display Filtered Properties */}
              {loading ? (
                <div
                  className="d-flex justify-content-center align-items-center py-5"
                  style={{ minHeight: "300px" }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : paginatedProperties.length > 0 ? (
                paginatedProperties.map((property, index) => (
                  <div className="mb-4" key={property.id || index}>
                    <PropertyListCard property={property} />
                  </div>
                ))
              ) : (
                <div className="text-center py-5">
                  <p className="text-muted">
                    No properties found matching your criteria.
                  </p>
                  <button
                    className="btn btn-outline-primary rounded-0 mt-2"
                    onClick={resetFilters}
                  >
                    Reset All Filters
                  </button>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && paginatedProperties.length > 0 && (
                <div className="py-5 d-flex justify-content-center flex-wrap gap-2">
                  {/* First */}
                  <button
                    className="btn btn-outline-primary rounded-0"
                    disabled={currentPage === 1}
                    onClick={() => goToPage(1)}
                  >
                    &laquo;
                  </button>

                  {/* Previous */}
                  <button
                    className="btn btn-outline-primary rounded-0"
                    disabled={currentPage === 1}
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    &lsaquo;
                  </button>

                  {/* Visible pages */}
                  {visiblePages.map((page) => (
                    <button
                      key={page}
                      className={`btn rounded-0 ${
                        currentPage === page
                          ? "btn-light border-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ))}

                  {/* Ellipsis */}
                  {visiblePages.length > 0 &&
                    visiblePages[visiblePages.length - 1] < totalPages - 2 && (
                      <button
                        className="btn btn-outline-secondary rounded-0"
                        disabled
                      >
                        ...
                      </button>
                    )}

                  {/* Last 2 pages */}
                  {totalPages > 3 &&
                    [totalPages - 1, totalPages].map((page) => {
                      if (!visiblePages.includes(page) && page > 0) {
                        return (
                          <button
                            key={page}
                            className={`btn rounded-0 ${
                              currentPage === page
                                ? "btn-light border-primary"
                                : "btn-outline-primary"
                            }`}
                            onClick={() => goToPage(page)}
                          >
                            {page}
                          </button>
                        );
                      }
                      return null;
                    })}

                  {/* Next */}
                  <button
                    className="btn btn-outline-primary rounded-0"
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    &rsaquo;
                  </button>

                  {/* Last */}
                  <button
                    className="btn btn-outline-primary rounded-0"
                    disabled={currentPage === totalPages}
                    onClick={() => goToPage(totalPages)}
                  >
                    &raquo;
                  </button>
                </div>
              )}
            </div>

            {/* Desktop Right Sidebar */}
            <div className="d-none d-lg-block col-lg-3">
              {renderDesktopAdvancedFilters()}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}